import { useLocalization } from "@fluent/react";
import { Button, Chip } from "@narmi/design_system";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";
import cc from "classcat";

import BalancesPlusDropdown from "../../../../components/dashboard/BalancesPlusDropdown";
import styles from "../AccountsModule.module.scss";

import ViewToggle from "./ViewToggle";

import type { ViewMode } from "../useViewPreference";
import type { FilterKey } from "./types";

export const FILTERS: { key: FilterKey; labelKey: string }[] = [
  { key: "all", labelKey: "dashboard-redesign-accounts-filter-all" },
  { key: "favorites", labelKey: "account-group-favorites" },
  { key: "checking", labelKey: "account-group-checking" },
  { key: "savings", labelKey: "account-group-savings" },
  { key: "credit_cards", labelKey: "account-group-credit-cards" },
  { key: "loans", labelKey: "account-group-loans" },
  { key: "cds", labelKey: "account-group-cds" },
];

type AccountsSectionHeaderProps = {
  title: string;
  view: ViewMode;
  onViewChange: (mode: ViewMode) => void;
  isEditing: boolean;
  onEditToggle: () => void;
  showPlusButton?: boolean;
  filter?: FilterKey;
  onFilterChange?: (f: FilterKey) => void;
  availableFilters?: FilterKey[];
  isSmall?: boolean;
  onTitleClick?: () => void;
};

const AccountsSectionHeader = ({
  title,
  view,
  onViewChange,
  isEditing,
  onEditToggle,
  showPlusButton = false,
  filter,
  onFilterChange,
  availableFilters,
  isSmall = false,
  onTitleClick,
}: AccountsSectionHeaderProps) => {
  const { l10n } = useLocalization();
  const institution = useInstitution();
  const hasEntryPoints =
    (institution?.account_opening_entry_points_config?.balances_dropdown ?? [])
      .length > 0;
  const visibleFilters = availableFilters
    ? FILTERS.filter(({ key }) => availableFilters.includes(key))
    : FILTERS;
  const canRenderFilterChips =
    !isSmall && filter !== undefined && Boolean(onFilterChange);
  const isFilterChipsVisible =
    canRenderFilterChips && !isEditing && view !== "list";
  const isEditToggleHidden = view === "list";

  return (
    <div className={styles.sectionHeader}>
      <div
        className={cc([
          styles.sectionHeaderLeft,
          !isFilterChipsVisible && styles.sectionHeaderLeftFiltersHidden,
        ])}
      >
        <div className={styles.sectionHeaderTitleRow}>
          {onTitleClick ? (
            <button
              type="button"
              className={`${styles.sectionHeaderTitle} ${styles.sectionHeaderTitleLink} button--reset`}
              onClick={onTitleClick}
            >
              {title}
              <span
                className={`narmi-icon-chevron-right ${styles.sectionHeaderTitleLinkIcon}`}
              />
            </button>
          ) : (
            <div className={styles.sectionHeaderTitle}>{title}</div>
          )}
          {!isSmall &&
            showPlusButton &&
            (hasEntryPoints ? <BalancesPlusDropdown /> : null)}
        </div>
        {canRenderFilterChips && (
          <div
            aria-hidden={!isFilterChipsVisible}
            className={cc([
              styles.filterChips,
              !isFilterChipsVisible && styles.filterChipsHidden,
            ])}
          >
            {visibleFilters.map(({ key, labelKey }) => (
              <div key={key} className={styles.filterChip}>
                <Chip
                  label={l10n.getString(labelKey)}
                  kind={filter === key ? "primary" : "secondary"}
                  onClick={
                    isFilterChipsVisible
                      ? () => onFilterChange?.(filter === key ? "all" : key)
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {!isSmall && (
        <div className={styles.sectionHeaderActions}>
          <div
            aria-hidden={isEditToggleHidden}
            inert={isEditToggleHidden}
            className={cc([
              isEditToggleHidden && styles.sectionHeaderEditToggleHidden,
            ])}
          >
            <Button
              kind="negative"
              size="s"
              label={
                isEditing
                  ? l10n.getString("dashboard-redesign-accounts-done")
                  : l10n.getString("dashboard-redesign-accounts-edit")
              }
              onClick={isEditToggleHidden ? undefined : onEditToggle}
            />
          </div>
          <ViewToggle view={view} onChange={onViewChange} />
        </div>
      )}
    </div>
  );
};

export default AccountsSectionHeader;
